import { API_BASE_URL } from '@/lib/api-base';
import { buildClaudeCommand, mcpUrl } from '@/lib/mcp-access';
import type { CreatedMcpToken, McpEnvironment } from '@mss_creator/shared';
import { ActionBar, Alert, Button, Dialog, TextField } from '@pms-digital/design-system';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { type McpCopyResult, McpCopyStatus } from './mcp-copy-status';

/** How long a copy confirmation stays written. */
const STATUS_MS = 4000;

type CopyTarget = 'token' | 'command';

interface McpTokenRevealDialogProps {
  created: CreatedMcpToken;
  environment: McpEnvironment;
  /** Where focus goes once the dialog is closed (the « Créer un jeton » button). */
  returnFocusTo: RefObject<HTMLElement | null>;
  onDone: () => void;
}

/**
 * The one and only time the token's value is shown (spec D4). Only « Terminé »
 * closes it: no Escape, no click on the backdrop and no close button (the
 * `informational` tone has none), so the value cannot vanish by accident. The
 * Claude Code command is ready to paste, token included; each copy button
 * answers in a live region beside it (« Jeton copié »), for a few seconds.
 */
export function McpTokenRevealDialog({
  created,
  environment,
  returnFocusTo,
  onDone,
}: McpTokenRevealDialogProps) {
  const [results, setResults] = useState<Record<CopyTarget, McpCopyResult | null>>({
    token: null,
    command: null,
  });
  const timers = useRef<Partial<Record<CopyTarget, ReturnType<typeof setTimeout>>>>({});
  const command = buildClaudeCommand(
    environment,
    mcpUrl(API_BASE_URL, window.location.origin),
    created.value,
  );

  useEffect(
    () => () => {
      for (const timer of Object.values(timers.current)) clearTimeout(timer);
    },
    [],
  );

  async function copy(target: CopyTarget, text: string, label: string) {
    let result: McpCopyResult;
    try {
      await navigator.clipboard.writeText(text);
      result = { kind: 'copied', label };
    } catch {
      result = { kind: 'failed' };
    }
    clearTimeout(timers.current[target]);
    setResults((previous) => ({ ...previous, [target]: result }));
    timers.current[target] = setTimeout(
      () => setResults((previous) => ({ ...previous, [target]: null })),
      STATUS_MS,
    );
  }

  return (
    <Dialog
      open
      size="form"
      tone="informational"
      icon="key"
      title={`Votre jeton « ${created.token.name} »`}
      returnFocusTo={returnFocusTo}
      actions={<ActionBar align="center" primary={<Button onClick={onDone}>Terminé</Button>} />}
    >
      <div className="flex flex-col gap-4 text-left">
        <Alert tone="warning" title="Copiez ce jeton maintenant.">
          Il ne sera plus jamais affiché. Perdu, il se révoque et se remplace.
        </Alert>
        <div className="flex flex-col gap-2">
          <div className="flex items-end gap-3">
            <div className="min-w-0 flex-1">
              <TextField label="Jeton" readOnly value={created.value} autoComplete="off" />
            </div>
            <Button
              variant="primary"
              icon="content_copy"
              onClick={() => void copy('token', created.value, 'Jeton copié')}
            >
              Copier
            </Button>
          </div>
          <McpCopyStatus result={results.token} />
        </div>
        <div className="flex flex-col gap-2">
          <p id="mcp-command-label" className="m-0 text-noir text-small">
            {`Commande Claude Code (${environment})`}
          </p>
          <pre
            aria-labelledby="mcp-command-label"
            className="m-0 whitespace-pre-wrap break-all rounded-md border border-gray-200 bg-gray-50 p-4 text-noir text-small"
          >
            {command}
          </pre>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              icon="content_copy"
              onClick={() => void copy('command', command, 'Commande copiée')}
            >
              Copier la commande
            </Button>
            {environment === 'dev' && (
              <span className="text-caption text-gray-600">
                Remplacez la partie entre chevrons avant de la lancer.
              </span>
            )}
            <McpCopyStatus result={results.command} />
          </div>
        </div>
      </div>
    </Dialog>
  );
}
